import { NoResults, Spinner } from "client/Components";
import { DataSourceSortableState } from "client/Data/sorting";
import { useForceRerender } from "client/Hooks";
import { isNil } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	ReactElement,
	useEffect,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { HubItemCategoryModel } from "client/Api/Hub/hub";
import {
	ActiveFilter,
	HubItemIsNewFilter,
	hubItemIsNewToFlag
} from "../../common";
import { List } from "../CategoryOverview/List/List";
import { createFilters, createOrder } from "../CategoryOverview/useDataSource";
import {
	getRelevantItems,
	KnowledgeItemDetailsViewModel,
	KnowledgeItemOrderModel,
	KnowledgeItemPagingInformationModel,
	KnowledgeItemPreviewItemModel
} from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import styles from "./RelevantItems.module.less";
import { TimeZone } from "client/DateAndTime/timezones";

export type RelevantItemsProps = {
	item: KnowledgeItemDetailsViewModel | null;
	viewsCount: number | undefined;
	getCategory(categoryId: number | null): HubItemCategoryModel | undefined;
	activeFilter: ActiveFilter | null;
	onActiveFilterChange(filter: ActiveFilter): void;
	selectedCategoryId: number | null;
	onSelectedCategoryIdChange(categoryId: number): void;
	searchTerm: VersionedValue<string>;
	sorting: DataSourceSortableState<KnowledgeItemPreviewItemModel>;
	onNavigateToItem(id: number): void;
	timezone: TimeZone;
	isNewFilter: HubItemIsNewFilter;
};

export const RelevantItems = (props: RelevantItemsProps): ReactElement | null => {
	const {
		item,
		viewsCount,
		getCategory,
		activeFilter,
		onActiveFilterChange,
		selectedCategoryId,
		onSelectedCategoryIdChange,
		searchTerm,
		sorting,
		onNavigateToItem,
		timezone,
		isNewFilter
	} = props;

	const [state, setState] = useState<LoadingState>({
		kind: "loading"
	});

	const [retryHandler, itemsVersion] = useForceRerender();

	useEffect(
		(): (() => void) | undefined => {
			if (!item || isNil(viewsCount)) {
				return;
			}

			hasImplicitDependencyOn(itemsVersion);

			const load = async (signal: AbortSignal): Promise<void> => {
				try {
					setState({
						kind: "loading"
					});

					const pagingInformation: KnowledgeItemPagingInformationModel = {
						id: item.id,
						publicationDate: item.publicationDate,
						title: item.title,
						viewsCount,
						pinDate: item.pinDate,
						relevanceScore: null
					};

					const [authorId, tagId] = createFilters(activeFilter);

					const order: KnowledgeItemOrderModel | null = createOrder(sorting);


					const items: readonly KnowledgeItemPreviewItemModel[] = await getRelevantItems(
						item.id,
						pagingInformation,
						selectedCategoryId,
						authorId,
						tagId,
						searchTerm.value,
						order,
						hubItemIsNewToFlag[isNewFilter],
						signal
					);

					setState({
						kind: "success",
						items
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					console.error(e);

					setState({
						kind: "error"
					});
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return () => cancellation.abort();
		},
		[
			activeFilter,
			item,
			viewsCount,
			searchTerm.value,
			selectedCategoryId,
			sorting,
			itemsVersion,
			isNewFilter
		]
	);

	switch (state.kind)
	{
		case "loading":
			return (
				<div className={styles.spinnerBlock}>
					<Spinner />
				</div>
			);
		case "error":
			return (
				<NoResults
					trackingId="relevant-items-failure"
					icon="book"
					title={localize("_UnableToRetrieveItems")}
					message={localize("_WeWereUnableToRetrieveTheItems")}
					onRetry={retryHandler}
				/>
			);
		case "success":
			if (state.items.length === 0) {
				return (
					<NoResults
						trackingId="no-relevant-items"
						icon="book"
						title={localize("_NoItemsToShow")}
						message={localize("_ThereAreCurrentlyNoItemsToShowHere")}
					/>
				);
			}

			return (
				<List
					trackingId="knowledge-item-list-relevant"
					items={state.items}
					onNavigateToItem={onNavigateToItem}
					getCategory={getCategory}
					onActiveFilterChange={onActiveFilterChange}
					onSelectedCategoryIdChange={onSelectedCategoryIdChange}
					timezone={timezone}
				/>
			);
		default:
			never(state);
	}
};
RelevantItems.displayName = "RelevantItems";

type LoadingState =
	| { kind: "loading"; }
	| { kind: "error"; }
	| { kind: "success"; items: readonly KnowledgeItemPreviewItemModel[]; };
